<template>
  <div>
    <h3 class="text-lg font-medium mb-6">Mes Commandes</h3>

    <div v-if="pending" class="space-y-4">
      <USkeleton v-for="i in 3" :key="i" class="h-24 w-full" />
    </div>

    <div v-else-if="orders && orders.length > 0" class="space-y-4">
      <div
        v-for="order in orders"
        :key="order.id"
        class="bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 overflow-hidden cursor-pointer hover:border-main-color transition-colors"
        @click="openOrderDetails(order)"
      >
        <div
          class="p-4 flex flex-col md:flex-row justify-between md:items-center gap-4"
        >
          <div class="flex items-center gap-4">
            <!-- Show first item image as preview -->
            <div class="flex -space-x-4 overflow-hidden py-1">
              <template
                v-for="(item, idx) in order.items.slice(0, 3)"
                :key="idx"
              >
                <NuxtImg
                  v-if="(item.meal || item.menu)?.image"
                  :src="getImageUrl((item.meal || item.menu)?.image)"
                  class="inline-block h-12 w-12 rounded-full ring-2 ring-white dark:ring-gray-800 object-cover"
                />
              </template>
              <div
                v-if="order.items.length > 3"
                class="flex items-center justify-center h-12 w-12 rounded-full ring-2 ring-white dark:ring-gray-800 bg-gray-100 dark:bg-gray-700 text-xs font-medium text-gray-500"
              >
                +{{ order.items.length - 3 }}
              </div>
            </div>

            <div>
              <div class="flex items-center gap-3 mb-1">
                <span class="font-bold text-gray-900 dark:text-white">
                  #{{ order.id.slice(0, 8) }}
                </span>
                <UBadge :color="getStatusColor(order.status)" variant="subtle">
                  {{ formatStatus(order.status) }}
                </UBadge>
              </div>
              <p class="text-sm text-gray-500">
                {{ new Date(order.createdAt).toLocaleDateString() }} à
                {{ new Date(order.createdAt).toLocaleTimeString() }}
              </p>
            </div>
          </div>

          <div class="flex items-center justify-between md:gap-8">
            <span class="font-bold text-lg text-main-color">
              {{ Number(order.total).toFixed(2) }}€
            </span>

            <UButton
              color="main"
              variant="soft"
              size="sm"
              icon="i-lucide-rotate-cw"
              @click.stop="reorder(order)"
            >
              Commander à nouveau
            </UButton>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="text-center py-10 text-gray-500">
      Aucune commande passée.
    </div>

    <!-- Order Details Modal -->
    <UModal v-model:open="isDetailsOpen">
      <template #content>
        <UCard v-if="selectedOrder">
          <template #header>
            <div class="flex items-center justify-between">
              <h3 class="text-lg font-semibold cursor-default">
                Commande #{{ selectedOrder.id.slice(0, 8) }}
              </h3>
              <UButton
                color="gray"
                variant="ghost"
                icon="i-heroicons-x-mark-20-solid"
                class="-my-1"
                @click="isDetailsOpen = false"
              />
            </div>
            <p class="text-sm text-gray-500 mt-1">
              {{ new Date(selectedOrder.createdAt).toLocaleString() }}
            </p>
          </template>

          <div class="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div
              v-for="(item, idx) in selectedOrder.items"
              :key="idx"
              class="flex gap-4 border-b border-gray-100 dark:border-gray-700 last:border-0 pb-4 last:pb-0"
            >
              <NuxtImg
                v-if="(item.meal || item.menu)?.image"
                :src="getImageUrl((item.meal || item.menu)?.image)"
                class="w-16 h-16 rounded-lg object-cover bg-gray-100 flex-shrink-0"
              />
              <div class="flex-1">
                <div class="flex justify-between items-start">
                  <h4 class="font-medium text-sm">
                    {{ item.quantity }}x
                    {{ (item.meal || item.menu)?.name || item.options?.name }}
                  </h4>
                  <span class="font-semibold text-sm">
                    {{ (Number(item.price) * item.quantity).toFixed(2) }}€
                  </span>
                </div>

                <p
                  v-if="item.options?.note"
                  class="text-xs text-gray-500 mt-1 italic"
                >
                  Note: {{ item.options.note }}
                </p>
              </div>
            </div>

            <div
              class="flex justify-between pt-4 border-t border-gray-100 dark:border-gray-700 font-bold"
            >
              <span>Total</span>
              <span class="text-main-color"
                >{{ Number(selectedOrder.total).toFixed(2) }}€</span
              >
            </div>
          </div>

          <template #footer>
            <UButton block color="main" @click="reorder(selectedOrder)">
              Commander à nouveau
            </UButton>
          </template>
        </UCard>
      </template>
    </UModal>
    <!-- Reorder Confirmation Modal -->
    <UModal v-model:open="isReorderConfirmOpen">
      <template #content>
        <UCard>
          <template #header>
            <h3 class="text-lg font-semibold">Confirmer la commande</h3>
          </template>

          <div class="py-4">
            <p>
              Voulez-vous vraiment ajouter ces articles à votre panier actuel ?
            </p>
          </div>

          <template #footer>
            <div class="flex justify-end gap-3">
              <UButton
                color="gray"
                variant="soft"
                @click="isReorderConfirmOpen = false"
              >
                Annuler
              </UButton>
              <UButton color="main" @click="confirmReorder">
                Confirmer
              </UButton>
            </div>
          </template>
        </UCard>
      </template>
    </UModal>
  </div>
</template>

<script setup lang="ts">
const config = useRuntimeConfig();
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const { token } = useAuth();
const cartStore = useCartStore();
const toast = useToast();
const router = useRouter();

const orders = ref<any[]>([]); // eslint-disable-line @typescript-eslint/no-explicit-any
const pending = ref(true);

// Modal state
const isDetailsOpen = ref(false);
const selectedOrder = ref<any>(null); // eslint-disable-line @typescript-eslint/no-explicit-any

// Reorder confirmation state
const isReorderConfirmOpen = ref(false);
const orderToReorder = ref<any>(null); // eslint-disable-line @typescript-eslint/no-explicit-any

const getImageUrl = (path: string) => {
  if (!path) return undefined;
  if (path.startsWith("http")) return path;
  const apiUrl = config.public.apiUrl || "http://localhost:4200";
  return `${apiUrl}/${path.startsWith("/") ? path.slice(1) : path}`;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const openOrderDetails = (order: any) => {
  selectedOrder.value = order;
  isDetailsOpen.value = true;
};

const fetchOrders = async () => {
  pending.value = true;
  try {
    const { $api } = useNuxtApp();
    const data = await $api.get<any[]>("/cart-history/my-orders", "include"); // eslint-disable-line @typescript-eslint/no-explicit-any
    orders.value = data || [];
  } catch (e) {
    console.error(e);
  } finally {
    pending.value = false;
  }
};

onMounted(() => {
  fetchOrders();
});

const getStatusColor = (status: string) => {
  switch (status) {
    case "PENDING":
      return "orange";
    case "ACCEPTED":
    case "PREPARING":
    case "READY":
    case "DELIVERING":
      return "blue";
    case "DELIVERED":
      return "green";
    case "CANCELED":
      return "red";
    default:
      return "gray";
  }
};

const formatStatus = (status: string) => {
  switch (status) {
    case "PENDING":
      return "En attente";
    case "ACCEPTED":
      return "Acceptée";
    case "PREPARING":
      return "En préparation";
    case "READY":
      return "Prête";
    case "DELIVERING":
      return "En livraison";
    case "DELIVERED":
      return "Livrée";
    case "CANCELED":
      return "Annulée";
    default:
      return status;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const reorder = (order: any) => {
  if (isDetailsOpen.value) {
    isDetailsOpen.value = false;
  }
  orderToReorder.value = order;
  isReorderConfirmOpen.value = true;
};

const confirmReorder = () => {
  if (!orderToReorder.value) return;
  const order = orderToReorder.value;
  isReorderConfirmOpen.value = false;

  let addedCount = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  order.items.forEach((item: any) => {
    if (item.options) {
      // Reconstruct CartItem correctly using enriched backend data if available
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cartItem: any = {
        ...item.options,
        id: `history-${item.id}-${Date.now()}`,
        type: item.itemType,
        quantity: item.quantity,
        totalPrice: item.price * item.quantity,
      };

      if (item.itemType === "regular" || item.itemType === "customized") {
        if (item.meal) {
          cartItem.meal = {
            ...item.meal,
            image: getImageUrl(item.meal.image),
          };
        } else {
          // Fallback if backend enrichment fails
          cartItem.meal = {
            id: item.itemId,
            name: item.options.name || "Article",
          };
        }
      } else if (item.itemType === "menu") {
        if (item.menu) {
          cartItem.menu = {
            ...item.menu,
            image: getImageUrl(item.menu.image),
          };
        } else {
          cartItem.menu = {
            id: item.itemId,
            name: item.options.name || "Menu",
          };
        }
      }

      cartStore.addFromHistory(cartItem);
      addedCount++;
    }
  });

  if (addedCount > 0) {
    toast.add({ title: "Articles ajoutés au panier", color: "green" });
    router.push("/checkout");
  } else {
    toast.add({ title: "Impossible de récupérer les articles", color: "red" });
  }
  orderToReorder.value = null;
};
</script>
